import Message from "@/types/Message.interface"
import Pagination from "@/types/Pagination.interface"
import { MessagesTableParams } from "@/types/PaginationParams.interface"

interface State {
    messagesTableMessages: Message[]
    messagesTableMessagesCurrentPage: number
    messagesTableMessagesLastPage: number
    messagesTableMessagesLoading: boolean
    messagesTableMessagesDelivered: number
    messagesTableBroadcastsSent: number
    messagesTableMessagesRecieved: number 
    messagesTableContactsReached: number
    messagesTableParams: MessagesTableParams
    messagesTableParamsExportDefault: MessagesTableParams
}

interface MessageTableTopRowData {
    messagesDelivered: number
    broadcastsSent: number 
    messagesRecieved: number
    contactsReached: number
}

const defaults = () => {
    return {
        messagesTableMessages: [],
        messagesTableMessagesCurrentPage: 1,
        messagesTableMessagesLastPage: null,
        messagesTableMessagesLoading: false,
        messagesTableMessagesDelivered: 0,
        messagesTableBroadcastsSent: 0,
        messagesTableMessagesRecieved: 0,
        messagesTableContactsReached: 0,
        messagesTableParams: {
            page: 1,
            limit: 15,
            columnsType: ['delivered','outbound','from','to','text','type'],
            deliveredMessages: ['descending'],
            outboundMessages: null,
            status: null, 
            messagesType: null, 
            search: null,
            // Sets the start date to 1970 jan 1 0.00.00 
            startDate: new Date(null).toISOString(),
            endDate: new Date().toISOString()
        },
        // These cannot and shouldn't ever be changed, these properties are our default properties which we use when a user wants to export 
        // a general message feed table without filters applied. Also used to reset filters when user leaves page
        messagesTableParamsExportDefault: {
            page: 1,
            limit: 15,
            columnsType: ['delivered','outbound','from','to','text','type'],
            deliveredMessages: ['descending'],
            outboundMessages: null,
            status: null, 
            messagesType: null, 
            search: null,
            startDate: new Date(null).toISOString(),
            endDate: new Date().toISOString()
        }
    }
}

export default {
    state: defaults(),
    mutations: {
        messagesTableDataPush(state: State, data: MessageTableTopRowData): void {
            // Sets our four data points which is used in ReportingItems.vue
            state.messagesTableMessagesDelivered = data.messagesDelivered
            state.messagesTableBroadcastsSent = data.broadcastsSent
            state.messagesTableMessagesRecieved = data.messagesRecieved
            state.messagesTableContactsReached = data.contactsReached
        },
        messagesTableMessagesPush(state: State, messages: Message[]): void {
            //  Update the state
            state.messagesTableMessages = window.removeDuplicatesById(state.messagesTableMessages, messages)
        },
        messagesTableMessagesRemove(state: State, ids: number[]): void {
            //  Get our current list of messages
            const messages = state.messagesTableMessages

            //  If no messages, do nothing
            if (!messages) return

            //  Else, filter our our removed ids
            const filtered = messages.filter(x => !ids.includes(x.id))

            //  Set our messages to the filtered list
            state.messagesTableMessages = filtered
        },
        messagesTableMessagesPaginationPush(state: State, page: Pagination) {
            // This is needed so that the filters are actually applied to the data table, otherwise pagination doesn't occur until refresh or page change
            state.messagesTableMessages = []

            state.messagesTableMessagesCurrentPage = page.current_page
            state.messagesTableMessagesLastPage = page.last_page
        },
        messagesTableParamsSet(state: State, params: MessagesTableParams): void {
            state.messagesTableParams = params
        },
        messagesTableMessagesLoadingSet (state: State, bool: boolean): void {
            state.messagesTableMessagesLoading = bool
        },
        messagesTableMessagesSync(state: State, updates: Message[]): void {
            //  Get our current params
            const params = state.messagesTableParams

            //  Determine our sorting order
            const desc = Object.values(params.deliveredMessages).includes('descending')

            //  Apply our inline filtering
            const filtered = updates.appSyncFilter(params, 'messagesTable')

            //  Filtered contains our list of nessages to update/push so we need a difference of items to remove that aren't within current applied filters
            const remove = window._.differenceBy(updates, filtered, 'id')

            //  Sync the state so new messages are included
            state.messagesTableMessages = window.removeDuplicatesById(state.messagesTableMessages, filtered, remove)

            //  Sort the state based off when the messages were created
            state.messagesTableMessages.sort((a: Message, b: Message) => {
                return desc
                //  @ts-ignore
                    ? +window.getDate(b.created_at) - +window.getDate(a.created_at)
                    //@ts-ignore
                    : +window.getDate(a.created_at) - +window.getDate(b.created_at)
            })
        }
    },
    actions: {
        messagesTableCsvExport ({ getters }, defaultParams: boolean): Promise<string[]> {
            //  Select our export filter params (if no filters applied, use default params)
            let payload = null; 
            if(!defaultParams){
                payload = getters.messagesTableParams
            } 
            else {
                payload = getters.messagesTableParamsExportDefault
            }

            //  Assemble our API endpoint
            const url = `${getters.api}/messages-table-export`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Just return our response from the server
                return response.data
            }).catch(() => null)
        },
        messagesTableGetTopRowData({ commit, getters }) {
            //  Assemble the API endpoint
            const url = `${getters.api}/messages-table-data`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Update our top row data 
                commit('messagesTableDataPush', response.data)

                //  Return our toprow data 
                return response.data
            }).catch(e => console.error("MessagesTableMessageGetTopRowData Error", e))
        },
        messagesTableMessagesPaginate({ commit, getters }) {
            //  Select our pagination params (if no input, use default params)
            const payload = getters.messagesTableParams
            
            //  Start our loading
            commit('messagesTableMessagesLoadingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/messages-table`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Update pagination params
                commit('messagesTableMessagesPaginationPush', response.data)

                //  Set the inbox selected messages
                commit('messagesTableMessagesPush', response.data.data)

                //  Stop our loading
                commit('messagesTableMessagesLoadingSet', false)

                //  Return our messages
                return response.data.data
            }).catch(e => console.error("MessagesTableMessagePaginate ERROR", e))
        }
    },
    getters: {
        messagesTableMessages (state: State): Message[] {
            return state.messagesTableMessages
        },
        messagesTableMessagesCurrentPage (state: State): number {
            return state.messagesTableMessagesCurrentPage
        },
        messagesTableMessagesLastPage (state: State): number {
            return state.messagesTableMessagesLastPage
        },
        messagesTableMessagesLoading (state: State): boolean {
            return state.messagesTableMessagesLoading
        },
        messagesTableMessagesDelivered (state: State): number {
            return state.messagesTableMessagesDelivered
        },
        messagesTableParams(state: State): MessagesTableParams {
            return state.messagesTableParams
        },
        messagesTableParamsExportDefault(state: State): MessagesTableParams {
            return state.messagesTableParamsExportDefault
        },
        messagesTableBroadcastsSent (state: State): number {
            return state.messagesTableBroadcastsSent
        },
        messagesTableMessagesRecieved (state: State): number {
            return state.messagesTableMessagesRecieved
        },
        messagesTableContactsReached (state: State): number {
            return state.messagesTableContactsReached
        },
        messagesTableStartDate (state: State): Date {
            return state.messagesTableParams.startDate
        },
        messagesTableEndDate (state: State): Date {
            return state.messagesTableParams.endDate
        }
    }
}
